import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as ArrowNext } from "../../assets/icons/arrowNext.svg";
import loginBackground from "../../assets/images/login-background.png";
import { Auth } from "../../routes/Constants";
import APIVariables from "../../services/api-variables";
import { validateEmail } from "../../shared/helpers";
import Axios from "../../services/Axios";

const shellClass =
  "relative isolate flex min-h-screen min-h-dvh flex-col bg-[#0d5c52] bg-cover bg-center bg-fixed bg-no-repeat font-sans";
const cardClass =
  "login-z w-full max-w-[520px] rounded-[2.75rem] border border-white/30 bg-white/18 px-8 py-10 shadow-[0_8px_40px_rgba(0,0,0,0.18)] backdrop-blur-[22px] sm:px-12";
const cardStyle = { WebkitBackdropFilter: "blur(22px)" };
const labelClass = "ml-2 text-sm font-semibold text-white";
const inputClass =
  "w-full rounded-lg border-0 bg-white px-4 py-2 text-[15px] text-[#374151] shadow-inner outline-none ring-2 ring-transparent placeholder:text-gray-400 focus:ring-2 focus:ring-white/70";
const primaryButtonClass =
  "inline-flex h-[46px] min-w-[160px] cursor-pointer items-center justify-center rounded-full border-0 bg-[#00567a] px-6 text-lg font-bold text-white hover:bg-[#005eb8] disabled:bg-[#8c8c8c]";

const ResetPassword = () => {
  const [loginCredentials, setLoginCredentials] = useState({
    email: "",
  });

  const [message, setMessage] = useState({ text: "", isSuccess: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const checkInputs = () => {
    if (!validateEmail(loginCredentials.email)) {
      const emailError = { text: "Email is invalid", isSuccess: false };
      setMessage(emailError);
      return emailError;
    }
    setMessage({ text: "", isSuccess: false });
    return { text: "", isSuccess: false };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const errors = checkInputs();

    if (errors.text !== "") {
      setIsSubmitting(false);
      return;
    }

    const res = await Axios.post(APIVariables.USERS_PASSWORD_RESET, {
      email: loginCredentials.email,
    }).catch((error) => {
      setMessage({
        text:
          error?.response?.data?.email?.[0] ??
          error?.message ??
          "Something went wrong",
        isSuccess: false,
      });
      return null;
    });

    setIsSubmitting(false);

    if (res?.status === 200) {
      setMessage({
        text: res?.data?.message ?? res?.data?.email?.[0] ?? "Email sent",
        isSuccess: true,
      });
    }
  };

  const shellStyle = { backgroundImage: `url(${loginBackground})` };

  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-testid="auth-reset-page"
    >
      <main className="login-z flex flex-1 flex-col items-center justify-center px-4 pb-10 pt-2">
        <div className={cardClass} style={cardStyle} data-testid="auth-reset-card">
          {message.isSuccess ? (
            <div
              className="flex min-h-[200px] flex-col items-center justify-center text-center text-white"
              data-testid="auth-reset-success"
            >
              <h1
                className="mb-4 text-2xl font-semibold"
                data-testid="auth-reset-success-heading"
              >
                Email sent
              </h1>
              <p
                className="m-0 text-base leading-relaxed text-white/90"
                data-testid="auth-reset-success-message"
              >
                {message.text}
              </p>
              <Link
                to={Auth.LOGIN}
                className={`${primaryButtonClass} mt-6 no-underline`}
                data-testid="auth-reset-back-to-login-link"
              >
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              <p
                className="mb-5 mt-4 text-base leading-relaxed text-white/90"
                data-testid="auth-reset-description"
              >
                Please enter your email address below and we will send you a
                link to reset your password.
              </p>

              <form
                className="flex flex-col gap-5"
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-reset-form"
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="reset-request-email" className={labelClass}>
                    Email Address
                  </label>
                  <input
                    id="reset-request-email"
                    type="email"
                    autoComplete="email"
                    placeholder="Email"
                    onChange={handleChange}
                    name="email"
                    value={loginCredentials.email}
                    data-testid="auth-reset-email-input"
                    className={
                      inputClass +
                      (message.text.length > 0 ? " ring-amber-300" : "")
                    }
                  />
                  {message.text.length > 0 && (
                    <span
                      className="text-sm text-amber-100"
                      data-testid="auth-reset-error"
                    >
                      {message.text}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className={primaryButtonClass}
                  disabled={isSubmitting}
                  data-testid="auth-reset-submit-button"
                >
                  Reset
                </button>
              </form>

              <hr className="my-6 border-0 border-t border-white/30" />
              <a
                href={`${Auth.LOGIN}?register=1`}
                className="flex items-center justify-between gap-3 text-white no-underline transition hover:opacity-90"
                data-testid="auth-reset-register-link"
              >
                <span className="text-lg text-white/90">
                  Register for an account
                </span>
                <ArrowNext className="h-5 w-5 shrink-0" aria-hidden />
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
